import { Grid, Tab, Tabs } from '@mui/material';
import { OrderSide } from '@tassat/common-definitions';
import { Asset } from '@tassat/fred-definitions';
import { AllowedTransactionType } from '@tassat/fred-extended-definitions';
import { Formik, FormikProps } from 'formik';
import moment from 'moment';
import numeral from 'numeral';
import * as React from 'react';

import { validateTransactForm } from './submission';
import TabPanel from './tabPanel';
import { TransactFormContents } from './TransactFormContents';
import { IFormikProps, ITransactFormValues } from './types';
import { emptySelectOption } from '../helpers/constant';

export default function TransactFundsForm(props: IFormikProps) {
    const { classes, formProps } = props;

    const {
        accountPositionCcyBalance,
        accountPositionSecurityBalance,
        savedTransactionValues,
        currentWallet,
        handleTransact,
        currentFirm,
        isLiquidityFacilityAvailable,
        realTimePrice,
        navPrice,
        assetPair,
        entitledAssetsByType
    } = formProps;

    const toSelectOptions = (assets: Asset[]) => assets.map(asset => ({
        label: `${asset.Ticker} - ${asset.Description}`,
        value: asset.AssetId
    }));

    const buyAssets = toSelectOptions(entitledAssetsByType[AllowedTransactionType.Buy] ?? []);
    const sellAssets = toSelectOptions(entitledAssetsByType[AllowedTransactionType.Sell] ?? []);
    const subscribeAssets = toSelectOptions(entitledAssetsByType[AllowedTransactionType.Subscribe] ?? []);
    const redeemAssets = toSelectOptions(entitledAssetsByType[AllowedTransactionType.Redeem] ?? []);

    const showAllOrderTypes = !currentFirm.IsLiquidityFacility && isLiquidityFacilityAvailable;

    const [initialValues] = React.useState<ITransactFormValues>(savedTransactionValues || {
        Type: OrderSide.SUBSCRIPTION,
        Fee: 0,
        // currentTab starts at 0, which is always the Buy or Subscription tab (never Sell/Redeem).
        AssetType: (showAllOrderTypes ? buyAssets[0] : subscribeAssets[0]) ?? emptySelectOption,
        Amount: '',
        AmountWithFee: 0,
        Wallet: currentWallet,
        PrivateDescription: '',
        NeedsApproval: currentWallet.AccessControl || false,
        RequestTime: null
    });

    const [ currentTab, setCurrentTab ] = React.useState<number>(0);

    const transactOriginalValidations = (values: ITransactFormValues) => validateTransactForm(values, {
        assetPair: assetPair,
        accountPositionSecurityBalance,
        accountPositionCcyBalance,
        realTimePrice
    });

    return (
        <Formik
            initialValues={initialValues}
            onSubmit={async (values: ITransactFormValues) => {
                const submissionValues: ITransactFormValues = {
                    ...values,
                    NeedsApproval: currentWallet.AccessControl,
                    RequestTime: moment(),
                    Amount: numeral((values.Amount as string).substring(1))
                        .value()
                };

                if (handleTransact) {
                    handleTransact(submissionValues);
                }
            }}
            validate={(values: ITransactFormValues) => transactOriginalValidations(values)}
            validateOnChange
            validateOnMount
        >
            {
                (formContentsProps: FormikProps<ITransactFormValues>) => (
                    <Grid
                        item
                        xs={12}
                    >
                        {showAllOrderTypes ?
                            <Tabs
                                classes={{
                                    indicator: currentTab === 0 || currentTab === 2 ?
                                        classes.greenIndicator
                                        : classes.redIndicator,
                                    flexContainer: classes.tabContainer
                                }}
                                className={classes.userTabsContainer}
                                onChange={(e, newTab) => {
                                    setCurrentTab(newTab);
                                }}
                                value={currentTab}
                                variant='fullWidth'
                            >
                                <Tab
                                    className={classes.userTab}
                                    id='buyTab'
                                    label='Buy'
                                />
                                <Tab
                                    className={classes.userTab}
                                    id='sellTab'
                                    label='Sell'
                                />
                                <Tab
                                    className={classes.userTab}
                                    id='subscriptionTab'
                                    label='Subscription'
                                />
                                <Tab
                                    className={classes.userTab}
                                    id='redemptionTab'
                                    label='Redemption'
                                />
                            </Tabs>
                            : (
                                <Tabs
                                    classes={{
                                        indicator: currentTab === 0 ?
                                            classes.greenIndicator
                                            : classes.redIndicator,
                                        flexContainer: classes.tabContainer
                                    }}
                                    className={classes.userTabsContainer}
                                    onChange={(e, newTab) => {
                                        setCurrentTab(newTab);
                                    }}
                                    value={currentTab}
                                    variant='fullWidth'
                                >
                                    <Tab
                                        className={classes.userTab}
                                        id='subscriptionTab'
                                        label='Subscription'
                                    />
                                    <Tab
                                        className={classes.userTab}
                                        id='redemptionTab'
                                        label='Redemption'
                                    />
                                </Tabs>
                            )}
                        {showAllOrderTypes ?
                            <Grid
                                item
                                xs={12}
                            >

                                <TabPanel
                                    index={0}
                                    value={currentTab}
                                >
                                    <TransactFormContents
                                        {...formContentsProps}
                                        classes={classes}
                                        currentTab={currentTab}
                                        formProps={{
                                            ...formProps,
                                            assetTypes: buyAssets
                                        }}
                                        navPrice={navPrice}
                                        orderSide={OrderSide.BUY}
                                        realTimePrice={realTimePrice}
                                        transactType='buy'
                                    />
                                </TabPanel>
                                <TabPanel
                                    index={1}
                                    value={currentTab}
                                >
                                    <TransactFormContents
                                        {...formContentsProps}
                                        classes={classes}
                                        currentTab={currentTab}
                                        formProps={{
                                            ...formProps,
                                            assetTypes: sellAssets
                                        }}
                                        navPrice={navPrice}
                                        orderSide={OrderSide.SELL}
                                        realTimePrice={realTimePrice}
                                        transactType='sell'
                                    />
                                </TabPanel>
                                <TabPanel
                                    index={2}
                                    value={currentTab}
                                >
                                    <TransactFormContents
                                        {...formContentsProps}
                                        classes={classes}
                                        currentTab={currentTab}
                                        formProps={{
                                            ...formProps,
                                            assetTypes: subscribeAssets
                                        }}
                                        navPrice={navPrice}
                                        orderSide={OrderSide.SUBSCRIPTION}
                                        realTimePrice={realTimePrice}
                                        transactType='subscription'
                                    />
                                </TabPanel>
                                <TabPanel
                                    index={3}
                                    value={currentTab}
                                >
                                    <TransactFormContents
                                        {...formContentsProps}
                                        classes={classes}
                                        currentTab={currentTab}
                                        formProps={{
                                            ...formProps,
                                            assetTypes: redeemAssets
                                        }}
                                        navPrice={navPrice}
                                        orderSide={OrderSide.REDEEM}
                                        realTimePrice={realTimePrice}
                                        transactType='redemption'
                                    />
                                </TabPanel>
                            </Grid>
                            : (
                                <Grid
                                    item
                                    xs={12}
                                >
                                    <TabPanel
                                        index={0}
                                        value={currentTab}
                                    >
                                        <TransactFormContents
                                            {...formContentsProps}
                                            classes={classes}
                                            formProps={{
                                                ...formProps,
                                                assetTypes: subscribeAssets
                                            }}
                                            navPrice={navPrice}
                                            orderSide={OrderSide.SUBSCRIPTION}
                                            realTimePrice={realTimePrice}
                                            transactType='subscription'
                                        />
                                    </TabPanel>
                                    <TabPanel
                                        index={1}
                                        value={currentTab}
                                    >
                                        <TransactFormContents
                                            {...formContentsProps}
                                            classes={classes}
                                            formProps={{
                                                ...formProps,
                                                assetTypes: redeemAssets
                                            }}
                                            navPrice={navPrice}
                                            orderSide={OrderSide.REDEEM}
                                            realTimePrice={realTimePrice}
                                            transactType='redemption'
                                        />
                                    </TabPanel>
                                </Grid>
                            )}
                    </Grid>
                )
            }
        </Formik>
    );
}